/**
 * CycleConditionHealthPanel — the C3 portfolio rollup, per plan cycle.
 *
 * Groups a cycle's conditions by the commitment they hang on and renders each
 * commitment's strip, with one portfolio line above them ("6 active assumptions
 * at elevated risk; 2 decisions approaching review thresholds" in the PRD's
 * words, built from the record rather than asserted).
 *
 * This is the container the shared surfaces mount — POR dashboard, plan
 * narrative — so those files gain four lines each rather than a data-loading
 * block. It is read-only: everything it shows is derived, and the write paths
 * live in ConditionsPanel.
 */

import { useEffect, useMemo, useState } from 'react';
import { Panel } from '../../ds';
import ConditionHealthStrip from './ConditionHealthStrip';
import { rollUpConditions, toConditionInput } from '../../../lib/conditionObjects';
import {
  listConditionsForCycle,
  type ConditionHealthRow,
} from '../../../lib/conditionObjectsService';

export interface CycleConditionHealthPanelProps {
  planCycleId: string;
  /** Workspace review cadence in days; falls back to the core's default. */
  staleAfterDays?: number;
  /** Injected for tests; defaults to now. */
  now?: string;
  className?: string;
}

export default function CycleConditionHealthPanel({
  planCycleId,
  staleAfterDays,
  now,
  className = '',
}: CycleConditionHealthPanelProps) {
  const [rows, setRows] = useState<ConditionHealthRow[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void listConditionsForCycle(planCycleId).then((result) => {
      if (cancelled) return;
      setRows(result.rows);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [planCycleId]);

  const nowIso = now ?? new Date().toISOString();

  const byCommitment = useMemo(() => {
    const groups = new Map<string, { title: string; rows: ConditionHealthRow[] }>();
    for (const row of rows) {
      const key = row.item_id ?? row.decision_id ?? 'unattached';
      const existing = groups.get(key);
      if (existing) existing.rows.push(row);
      else groups.set(key, { title: row.subject_title ?? 'Untitled commitment', rows: [row] });
    }
    return [...groups.entries()];
  }, [rows]);

  const portfolio = useMemo(
    () => rollUpConditions(rows.map(toConditionInput), { now: nowIso, staleAfterDays }),
    [rows, nowIso, staleAfterDays],
  );

  // Nothing to say is better than an empty panel taking a column. Also the
  // honest state before migration 699 is applied, when the query returns
  // nothing at all.
  if (!loaded || rows.length === 0) return null;

  return (
    <Panel
      label="Conditions"
      title="What this plan rests on"
      count={rows.length}
      className={className}
    >
      {portfolio.headline && (
        <p className="mb-4 text-sm text-[var(--text-sec)]">
          Across {byCommitment.length} commitment{byCommitment.length === 1 ? '' : 's'}:{' '}
          {portfolio.headline}
        </p>
      )}
      <div className="space-y-5">
        {byCommitment.map(([key, group]) => (
          <div key={key}>
            <h4 className="mb-1 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-ter)]">
              {group.title}
            </h4>
            <ConditionHealthStrip
              rows={group.rows}
              subjectLabel={group.title}
              staleAfterDays={staleAfterDays}
              now={nowIso}
              bare
            />
          </div>
        ))}
      </div>
    </Panel>
  );
}
